// Buyer details form: shared types, lists with their fixed Gujarati words, and per-step checks.
// The server (functions/src/buyerForm.ts) repeats the full check before anything is saved for good.

export const GU_FIELDS = ['firstName', 'middleName', 'lastName', 'fatherName', 'addressLine1', 'addressLine2', 'city', 'state', 'occupationOther'] as const;
export type GuField = typeof GU_FIELDS[number];

export type Buyer = {
  key: string;
  customerId: string | null;
  firstName: string; middleName: string; lastName: string;
  fatherRelation: 'son' | 'daughter' | 'wife' | '';
  fatherName: string;
  dateOfBirth: string;
  gender: 'Male' | 'Female' | '';
  occupation: string; occupationOther: string;
  residentStatus: 'india' | 'nri' | '';
  pan: string; aadhaar: string;
  mobile: string; whatsappSame: boolean; whatsapp: string; email: string;
  addressLine1: string; addressLine2: string; city: string; state: string; pincode: string;
  sameAddress: boolean;
  relationToFirst: string;
  gu: Partial<Record<GuField, string>>;
  guFrom: Partial<Record<GuField, string>>;
  guOk: Partial<Record<GuField, boolean>>;
  /** Saved PAN / Aadhaar shown masked on the link form; empty input keeps them. */
  panHint?: string; aadhaarHint?: string;
};

export type DocKey = 'photo' | 'pan' | 'aadhaarFront' | 'aadhaarBack';
export type DocInfo = { path: string; url: string; contentType: string; size: number; uploadedAtMs: number };
export type DocsByBuyer = Record<string, Partial<Record<DocKey, DocInfo>>>;

export const DOCS: Array<{ key: DocKey; label: string; hint: string }> = [
  { key: 'photo', label: 'Photo', hint: 'A recent photo, face clearly visible' },
  { key: 'pan', label: 'PAN card', hint: 'Front side' },
  { key: 'aadhaarFront', label: 'Aadhaar, front', hint: 'Side with photo' },
  { key: 'aadhaarBack', label: 'Aadhaar, back', hint: 'Side with address' },
];

// Same list as the customer form in the booking drawer, so saved values match.
export const OCCUPATIONS: Array<{ value: string; label: string; gu: string }> = [
  { value: 'Business Owner', label: 'Business', gu: 'વેપાર' },
  { value: 'Salaried (Private)', label: 'Private job', gu: 'નોકરી' },
  { value: 'Salaried (Government)', label: 'Government job', gu: 'સરકારી નોકરી' },
  { value: 'Self-Employed', label: 'Self-employed', gu: 'સ્વરોજગાર' },
  { value: 'Professional (Doctor/CA/Lawyer)', label: 'Doctor / CA / Lawyer', gu: 'વ્યવસાયી' },
  { value: 'Freelancer/Consultant', label: 'Consultant', gu: 'કન્સલ્ટન્ટ' },
  { value: 'Homemaker', label: 'Homemaker', gu: 'ગૃહિણી' },
  { value: 'Retired', label: 'Retired', gu: 'નિવૃત્ત' },
  { value: 'Student', label: 'Student', gu: 'વિદ્યાર્થી' },
  { value: 'NRI/Overseas', label: 'Working abroad', gu: 'વિદેશમાં નોકરી/ધંધો' },
  { value: 'Other', label: 'Other', gu: '' },
];
export const occupationGu = (b: Pick<Buyer, 'occupation' | 'gu'>) =>
  b.occupation === 'Other' ? b.gu.occupationOther || '' : OCCUPATIONS.find((o) => o.value === b.occupation)?.gu || '';

export const RELATIONS_TO_FIRST = ['Spouse', 'Son', 'Daughter', 'Father', 'Mother', 'Brother', 'Sister', 'Other'];

export const STATES = [
  'Gujarat', 'Maharashtra', 'Rajasthan', 'Madhya Pradesh', 'Uttar Pradesh', 'Bihar', 'Delhi', 'Karnataka', 'Tamil Nadu',
  'Telangana', 'Andhra Pradesh', 'West Bengal', 'Punjab', 'Haryana', 'Odisha', 'Kerala', 'Jharkhand', 'Chhattisgarh',
  'Assam', 'Goa', 'Uttarakhand', 'Himachal Pradesh', 'Jammu and Kashmir', 'Dadra and Nagar Haveli and Daman and Diu', 'Other',
];

const GU_DIGITS = '૦૧૨૩૪૫૬૭૮૯';
export const guDigits = (v: string | number) => String(v).replace(/\d/g, (d) => GU_DIGITS[Number(d)]);

/** Whole years on the given day (the form date). */
export const ageOn = (dob: string, on: Date = new Date()): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob || '');
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  let age = on.getFullYear() - y;
  if (on.getMonth() + 1 < mo || (on.getMonth() + 1 === mo && on.getDate() < d)) age -= 1;
  return age >= 0 && age < 130 ? age : null;
};

export const newBuyer = (index: number): Buyer => ({
  key: `b${index}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
  customerId: null,
  firstName: '', middleName: '', lastName: '', fatherRelation: '', fatherName: '',
  dateOfBirth: '', gender: '', occupation: '', occupationOther: '', residentStatus: 'india',
  pan: '', aadhaar: '', mobile: '', whatsappSame: true, whatsapp: '', email: '',
  addressLine1: '', addressLine2: '', city: '', state: 'Gujarat', pincode: '',
  sameAddress: index > 0, relationToFirst: '',
  gu: {}, guFrom: {}, guOk: {},
});

export const fullName = (b: Pick<Buyer, 'firstName' | 'middleName' | 'lastName'>) => [b.firstName, b.middleName, b.lastName].filter(Boolean).join(' ');
export const fullNameGu = (b: Buyer) => [b.gu.firstName, b.gu.middleName, b.gu.lastName].filter(Boolean).join(' ');

export const formatMobile = (m: string) => (m.length === 10 ? `+91 ${m.slice(0, 5)} ${m.slice(5)}` : m);

/** English text a Gujarati field is made from. Co-buyers at buyer 1's address use buyer 1's. */
export const guSource = (b: Buyer, f: GuField): string => {
  if (f === 'occupationOther' && b.occupation !== 'Other') return '';
  return String(b[f] || '').trim();
};

export const needsGu = (b: Buyer, f: GuField) => !!guSource(b, f);
export const guPending = (b: Buyer, f: GuField) => needsGu(b, f) && (b.guFrom[f] || '') !== guSource(b, f);

export type Step = 'about' | 'work' | 'address' | 'docs';
export const STEPS: Array<{ key: Step; title: string }> = [
  { key: 'about', title: 'About you' },
  { key: 'work', title: 'Work, contact and ID' },
  { key: 'address', title: 'Home address' },
  { key: 'docs', title: 'Photo and ID copies' },
];

const STEP_GU: Record<Step, GuField[]> = {
  about: ['firstName', 'middleName', 'lastName', 'fatherName'],
  work: ['occupationOther'],
  address: ['addressLine1', 'addressLine2', 'city', 'state'],
  docs: [],
};

/** First problem on one step of one buyer, or null. */
export const stepProblem = (b: Buyer, index: number, step: Step, all: Buyer[]): string | null => {
  if (step === 'about') {
    if (!b.firstName.trim()) return 'Enter the first name.';
    if (!b.lastName.trim()) return 'Enter the surname.';
    if (!b.fatherRelation) return 'Choose son, daughter or wife of.';
    if (!b.fatherName.trim()) return "Enter the father's or husband's name.";
    const age = ageOn(b.dateOfBirth);
    if (age == null || new Date(`${b.dateOfBirth}T00:00:00`).getTime() > Date.now()) return 'Enter a valid date of birth.';
    if (!b.gender) return 'Choose gender.';
  }
  if (step === 'work') {
    if (!b.occupation) return 'Choose an occupation.';
    if (b.occupation === 'Other' && !b.occupationOther.trim()) return 'Write the occupation.';
    if (!b.residentStatus) return 'Choose where you live.';
    if (!(b.panHint && !b.pan) && !/^[A-Z]{5}\d{4}[A-Z]$/.test(b.pan)) return 'PAN must look like ABCDE1234F.';
    if (b.pan && all.some((o, i) => i !== index && o.pan === b.pan)) return 'This PAN is the same as another buyer.';
    if (!(b.aadhaarHint && !b.aadhaar) && !/^[2-9]\d{11}$/.test(b.aadhaar)) return 'Aadhaar must be 12 digits.';
    if (!/^[6-9]\d{9}$/.test(b.mobile)) return 'Enter a 10-digit mobile number.';
    if (!b.whatsappSame && b.whatsapp && !/^[6-9]\d{9}$/.test(b.whatsapp)) return 'WhatsApp number must be 10 digits.';
    if (b.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(b.email)) return 'Check the email address.';
  }
  if (step === 'address' && !(index > 0 && b.sameAddress)) {
    if (!b.addressLine1.trim()) return 'Enter house / flat and building.';
    if (!b.city.trim()) return 'Enter the city.';
    if (!b.state.trim()) return 'Choose the state.';
    if (!/^\d{6}$/.test(b.pincode)) return 'Pincode must be 6 digits.';
  }
  if (step === 'address' && index > 0 && !b.relationToFirst) return 'Choose relation to buyer 1.';
  for (const f of STEP_GU[step]) {
    if (step === 'address' && index > 0 && b.sameAddress) continue;
    if (!needsGu(b, f)) continue;
    if (guPending(b, f) || !b.gu[f]) return 'Gujarati is still being written. Wait a moment.';
    if (!b.guOk[f]) return 'Check the Gujarati and tap ✓ on every line.';
  }
  return null;
};

export const buyerDone = (b: Buyer, index: number, all: Buyer[]) =>
  STEPS.every((s) => !stepProblem(b, index, s.key, all));

/** Co-buyers marked "same address" take buyer 1's address and its Gujarati. */
export const withSharedAddress = (buyers: Buyer[]): Buyer[] => {
  const first = buyers[0];
  if (!first) return buyers;
  const fields: GuField[] = ['addressLine1', 'addressLine2', 'city', 'state'];
  return buyers.map((b, i) => {
    if (i === 0 || !b.sameAddress) return b;
    const next: Buyer = { ...b, addressLine1: first.addressLine1, addressLine2: first.addressLine2, city: first.city, state: first.state, pincode: first.pincode, gu: { ...b.gu }, guFrom: { ...b.guFrom }, guOk: { ...b.guOk } };
    fields.forEach((f) => {
      next.gu[f] = first.gu[f];
      next.guFrom[f] = first.guFrom[f];
      next.guOk[f] = first.guOk[f];
    });
    return next;
  });
};
